import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, TrendingUp, CheckCircle2, Clock, DollarSign, Briefcase, BarChart3, ExternalLink, FileDown, Send, X, FileText } from 'lucide-react';
import { analytics, auth, reports, statements } from '@/lib/api';
import { TOKEN_KEY } from '@/lib/api/_client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const DrilldownContext = React.createContext(null);

export default function AnalyticsPage() {
  const me = auth.currentUser();
  const canView = ['super_admin', 'Principal RMA', 'principal_rma', 'Case Manager'].includes(me?.role);
  const [months, setMonths] = React.useState(6);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [drill, setDrill] = React.useState(null);  // { kind, title, params?, data? }

  React.useEffect(() => {
    if (!canView) return;
    setData(null); setErr('');
    analytics.firm(months).then(setData).catch((e) => setErr(e?.response?.data?.detail || 'Failed to load analytics'));
  }, [months, canView]);

  const openDrill = React.useCallback(async (kind, title, params = {}) => {
    setDrill({ kind, title, params, data: null });
    try {
      const r = await analytics.drilldown(kind, { months, ...params });
      setDrill({ kind, title, params, data: r });
    } catch (e) {
      setDrill({ kind, title, params, error: e?.response?.data?.detail || 'Load failed', data: { items: [] } });
    }
  }, [months]);

  if (!canView) {
    return (
      <div className="p-8">
        <Card className="border-slate-200"><CardContent className="p-6">
          <div className="text-sm font-semibold text-[#1A3A5C]">Restricted</div>
          <div className="mt-1 text-xs text-slate-500">Firm analytics is limited to principals + case managers.</div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <DrilldownContext.Provider value={openDrill}>
    <div className="p-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Firm analytics</h1>
          <div className="text-sm text-slate-500">Your firm's story at a glance — matters, lodgements, revenue, and turnaround. Click any card for the underlying detail.</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger data-testid="analytics-months" className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            onClick={async () => {
              try {
                const token = localStorage.getItem(TOKEN_KEY);
                const r = await fetch(analytics.firmPdfUrl(months), {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) {
                setErr('Could not load PDF preview');
              }
            }}
            data-testid="analytics-pdf-preview"
          >
            <FileDown className="mr-1.5 h-3.5 w-3.5" /> Preview PDF
          </Button>
          <MonthlyPackButton months={months} />
          <StatementsButton />
        </div>
      </div>

      {err && <div data-testid="analytics-error" className="mt-4 rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}

      {!data && !err && (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
      )}

      {data && (
        <>
          <KPIRow s={data.summary} months={months} />
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <MonthlyTrendCard monthly={data.monthly_trend} months={months} />
            <RevenueBySubclassCard rows={data.revenue_by_subclass}
                                    total={data.summary.total_revenue_aud} months={months} />
          </div>
          <div className="mt-6">
            <TurnaroundBySubclassCard rows={data.turnaround_by_subclass}
                                       avg={data.summary.avg_turnaround_days} />
          </div>
        </>
      )}
      <DrilldownDialog drill={drill} onClose={() => setDrill(null)} />
    </div>
    </DrilldownContext.Provider>
  );
}

function KPIRow({ s, months }) {
  const openDrill = React.useContext(DrilldownContext);
  const cards = [
    { k: 'total_matters', label: 'Total matters', icon: Briefcase, tone: 'text-[#1A3A5C]', value: s.total_matters,
      drill: () => openDrill('total_matters', 'All matters') },
    { k: 'open_matters', label: 'Open matters', icon: TrendingUp, tone: 'text-sky-700', value: s.open_matters,
      drill: () => openDrill('open_matters', 'Open matters') },
    { k: 'closed_in_period', label: 'Closed in period', icon: CheckCircle2, tone: 'text-emerald-700', value: s.closed_in_period,
      drill: () => openDrill('closed_in_period', `Matters closed in last ${months} months`) },
    { k: 'lodgement_success_rate', label: 'Lodge success', icon: BarChart3, tone: 'text-[#0F766E]',
      value: s.lodgement_success_rate_pct == null ? '—' : `${s.lodgement_success_rate_pct}%`,
      sub: s.lodgement_success_rate_pct == null ? 'No lodgements yet' : `${s.lodgement_success_count} of ${s.lodgement_success_count + s.lodgement_fail_count}`,
      drill: s.lodgement_success_rate_pct == null ? null
        : () => openDrill('lodgement_success', `Successful lodgements (last ${months} months)`) },
    { k: 'total_revenue', label: 'Revenue (A$)', icon: DollarSign, tone: 'text-[#2ECC9A]',
      value: `A$${(s.total_revenue_aud || 0).toLocaleString()}`,
      drill: () => openDrill('revenue', `Paid invoices (last ${months} months)`) },
    { k: 'avg_turnaround', label: 'Avg turnaround', icon: Clock, tone: 'text-amber-600',
      value: s.avg_turnaround_days == null ? '—' : `${s.avg_turnaround_days}d`,
      sub: s.avg_turnaround_days == null ? 'No closed matters yet' : 'Created → closed',
      drill: s.avg_turnaround_days == null ? null
        : () => openDrill('closed_in_period', `Turnaround detail — closed matters (last ${months} months)`) },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {cards.map(({ k, label, icon: Ico, tone, value, sub, drill }) => (
        <Card key={k}
              className={`border-slate-200 ${drill ? 'cursor-pointer transition hover:border-[#2ECC9A] hover:shadow-md' : ''}`}
              onClick={drill || undefined}>
          <CardContent className="p-4" data-testid={`analytics-kpi-${k}`}>
            <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${tone}`}>
              <Ico className="h-3 w-3" /> {label}
            </div>
            <div className="mt-1 font-serif text-3xl font-bold text-[#1A3A5C]">{value}</div>
            {sub && <div className="mt-0.5 text-[10px] text-slate-500">{sub}</div>}
            {drill && <div className="mt-1 flex items-center gap-1 text-[9px] text-slate-400"><ExternalLink className="h-2.5 w-2.5" />Click for detail</div>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MonthlyTrendCard({ monthly }) {
  const W = 480; const H = 140; const PAD = 30;
  const maxCount = Math.max(1, ...monthly.map((m) => Math.max(m.opened, m.closed)));
  const bw = (W - PAD * 2) / monthly.length;

  return (
    <Card className="border-slate-200" data-testid="analytics-monthly-trend"><CardContent className="p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
        <TrendingUp className="h-4 w-4 text-[#2ECC9A]" /> Matters opened vs closed
      </div>
      <div className="mt-3 overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H + 30}`} className="w-full min-w-[440px]" role="img" aria-label="Matters opened vs closed">
          <line x1={PAD} y1={H} x2={W - PAD} y2={H} stroke="#e2e8f0" strokeWidth="1" />
          {monthly.map((m, i) => {
            const x = PAD + i * bw;
            const openedH = (m.opened / maxCount) * (H - 20);
            const closedH = (m.closed / maxCount) * (H - 20);
            return (
              <g key={m.key}>
                <rect x={x + bw * 0.15} y={H - openedH} width={bw * 0.32} height={openedH}
                      rx="1.5" fill="#1A3A5C" opacity="0.8"
                      data-testid={`analytics-trend-opened-${i}`}>
                  <title>{`${m.label}: opened ${m.opened}`}</title>
                </rect>
                <rect x={x + bw * 0.5} y={H - closedH} width={bw * 0.32} height={closedH}
                      rx="1.5" fill="#2ECC9A" opacity="0.85"
                      data-testid={`analytics-trend-closed-${i}`}>
                  <title>{`${m.label}: closed ${m.closed}`}</title>
                </rect>
                <text x={x + bw / 2} y={H + 14} textAnchor="middle" fontSize="9" fill="#64748b">{m.label}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="mt-2 flex gap-3 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-[#1A3A5C]" /> Opened</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-[#2ECC9A]" /> Closed</span>
      </div>
    </CardContent></Card>
  );
}

function RevenueBySubclassCard({ rows, total, months }) {
  const openDrill = React.useContext(DrilldownContext);
  return (
    <Card className="border-slate-200" data-testid="analytics-revenue-subclass"><CardContent className="p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
        <DollarSign className="h-4 w-4 text-[#2ECC9A]" /> Revenue by subclass
      </div>
      {rows.length === 0 ? (
        <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
          No paid invoices in this window yet.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {rows.map((r) => {
            const pct = total ? Math.round((r.amount_aud / total) * 100) : 0;
            return (
              <div key={r.subclass}
                   data-testid={`analytics-rev-${r.subclass}`}
                   onClick={() => openDrill('revenue_by_subclass', `Revenue for subclass ${r.subclass} (last ${months}m)`, { subclass: r.subclass })}
                   className="cursor-pointer rounded-md p-1 transition hover:bg-slate-50">
                <div className="flex justify-between text-xs">
                  <span className="font-mono text-[#1A3A5C]">{r.subclass}</span>
                  <span className="font-semibold text-[#0F766E]">A${r.amount_aud.toLocaleString()} <span className="text-[10px] text-slate-400">· {r.invoice_count} inv</span></span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-slate-100">
                  <div className="h-2 rounded-full bg-[#2ECC9A]" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-right text-[9px] text-slate-500">{pct}%</div>
              </div>
            );
          })}
        </div>
      )}
    </CardContent></Card>
  );
}


function DrilldownDialog({ drill, onClose }) {
  if (!drill) return null;
  const { title, data, kind, error } = drill;
  const loading = data === null;

  const downloadCsv = () => {
    if (!data?.items?.length) return;
    const keys = Object.keys(data.items[0]).filter((k) => typeof data.items[0][k] !== 'object' || data.items[0][k] === null);
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [keys.join(','), ...data.items.map((r) => keys.map((k) => escape(r[k])).join(','))];
    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
  };

  const renderRows = () => {
    if (!data?.items || data.items.length === 0) {
      return <div className="rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">No records match this KPI in the selected window.</div>;
    }
    // Invoice-like rows
    if (['revenue', 'revenue_by_subclass'].includes(kind)) {
      return (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Invoice</th>
              <th className="px-3 py-2 font-medium">Client / Matter</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th>
              <th className="px-3 py-2 font-medium">Paid</th>
            </tr>
          </thead>
          <tbody data-testid="analytics-drilldown-tbody">
            {data.items.map((i) => (
              <tr key={i.id} className="border-t border-slate-100" data-testid={`drilldown-row-${i.id}`}>
                <td className="px-3 py-2 font-mono text-[11px]">{i.invoice_number}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-[#1A3A5C]">{i.client_name || '—'}</div>
                  <div className="text-[10px] text-slate-500">{i.matter_ref || '—'} · {i.description || ''}</div>
                </td>
                <td className="px-3 py-2 text-right font-semibold text-[#0F766E]">A${i.amount_aud.toLocaleString()}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{i.paid_at ? new Date(i.paid_at).toLocaleDateString('en-AU') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    // Lodgement rows
    if (['lodgement_success', 'lodgement_fail'].includes(kind)) {
      return (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Matter</th>
              <th className="px-3 py-2 font-medium">Subclass</th>
              <th className="px-3 py-2 font-medium">Attempts</th>
              <th className="px-3 py-2 font-medium">Finished</th>
              {kind === 'lodgement_fail' && <th className="px-3 py-2 font-medium">Error</th>}
            </tr>
          </thead>
          <tbody data-testid="analytics-drilldown-tbody">
            {data.items.map((j) => (
              <tr key={j.id} className="border-t border-slate-100" data-testid={`drilldown-row-${j.id}`}>
                <td className="px-3 py-2 font-mono text-[11px]">{j.matter_ref}</td>
                <td className="px-3 py-2">{j.subclass}</td>
                <td className="px-3 py-2">{j.attempts}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{j.updated_at ? new Date(j.updated_at).toLocaleString('en-AU') : '—'}</td>
                {kind === 'lodgement_fail' && <td className="px-3 py-2 text-[10px] text-red-600">{j.error || '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    // Matter rows
    return (
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-left text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Matter</th>
            <th className="px-3 py-2 font-medium">Applicant</th>
            <th className="px-3 py-2 font-medium">Subclass</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Created</th>
            <th className="px-3 py-2 font-medium">Closed</th>
          </tr>
        </thead>
        <tbody data-testid="analytics-drilldown-tbody">
          {data.items.map((m) => (
            <tr key={m.id} className="border-t border-slate-100" data-testid={`drilldown-row-${m.id}`}>
              <td className="px-3 py-2 font-mono text-[11px]">{m.ref}</td>
              <td className="px-3 py-2 text-[#1A3A5C]">{m.applicant || '—'}</td>
              <td className="px-3 py-2">{m.subclass || '—'}</td>
              <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] capitalize">{m.status || '—'}</Badge></td>
              <td className="px-3 py-2 text-[10px] text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString('en-AU') : '—'}</td>
              <td className="px-3 py-2 text-[10px] text-slate-500">{m.closed_at ? new Date(m.closed_at).toLocaleDateString('en-AU') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="analytics-drilldown-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {loading && !error ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
        ) : (
          <div>
            {error && <div className="mb-3 rounded-md bg-red-50 p-2 text-xs text-red-700">{error}</div>}
            <div className="flex items-center justify-between rounded-md bg-slate-50 p-2 text-[11px] text-slate-600">
              <div>
                <b>{data.total}</b> record{data.total === 1 ? '' : 's'}
                {data.total_amount_aud != null && <> · Total: <b>A${data.total_amount_aud.toLocaleString()}</b></>}
              </div>
              <button data-testid="analytics-drilldown-csv" onClick={downloadCsv}
                      disabled={!data.items?.length}
                      className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-[#1A3A5C] hover:bg-slate-100 disabled:opacity-50">
                Download CSV
              </button>
            </div>
            <div className="mt-3 max-h-[60vh] overflow-auto rounded-md border border-slate-200">
              {renderRows()}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TurnaroundBySubclassCard({ rows, avg }) {
  return (
    <Card className="border-slate-200" data-testid="analytics-turnaround-subclass"><CardContent className="p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
        <Clock className="h-4 w-4 text-amber-500" /> Average turnaround
        {avg != null && <Badge variant="outline" className="ml-2 border-amber-200 bg-amber-50 text-[10px] text-amber-700">Firm avg: {avg}d</Badge>}
      </div>
      {rows.length === 0 ? (
        <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
          No closed matters in this window yet.
        </div>
      ) : (
        <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Subclass</th>
                <th className="px-3 py-2 font-medium text-right">Avg days</th>
                <th className="px-3 py-2 font-medium text-right">Sample size</th>
                <th className="px-3 py-2 font-medium">vs firm avg</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const diff = avg != null ? (r.avg_days - avg) : 0;
                return (
                  <tr key={r.subclass} className="border-t border-slate-100" data-testid={`analytics-turn-${r.subclass}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#1A3A5C]">{r.subclass}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#1A3A5C]">{r.avg_days}d</td>
                    <td className="px-3 py-2 text-right text-slate-500">{r.sample}</td>
                    <td className="px-3 py-2">
                      {avg != null ? (
                        <span className={`text-[10px] ${diff > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                          {diff > 0 ? `+${diff.toFixed(1)}d slower` : `${Math.abs(diff).toFixed(1)}d faster`}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CardContent></Card>
  );
}

function MonthlyPackButton({ months }) {
  const [open, setOpen] = React.useState(false);
  const [recipients, setRecipients] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [includeEsign, setIncludeEsign] = React.useState(true);
  const [sending, setSending] = React.useState(false);

  const addEmail = () => {
    const v = input.trim();
    if (!v || recipients.includes(v) || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      if (v) toast.error('Enter a valid email');
      return;
    }
    setRecipients((r) => [...r, v]);
    setInput('');
  };

  const send = async () => {
    if (recipients.length === 0) { toast.error('Add at least one recipient'); return; }
    setSending(true);
    try {
      const r = await reports.sendMonthlyPack({
        recipients, months, include_esign: includeEsign, esign_days: 90,
      });
      toast.success(`Pack sent to ${r.sent_count}/${r.attempted} recipient(s)`);
      setOpen(false); setRecipients([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => setOpen(true)} data-testid="analytics-monthly-pack">
        <Send className="mr-1.5 h-3.5 w-3.5" /> Email partner pack
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="monthly-pack-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A3A5C]">Send monthly partner pack</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Bundles the firm analytics PDF{includeEsign ? ', eSign performance PDF' : ''} into a single branded email and delivers it to the partners you list below.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="partner@firm.com.au"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addEmail()}
                data-testid="monthly-pack-email-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addEmail} data-testid="monthly-pack-add-email">Add</Button>
            </div>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="monthly-pack-recipients">
                {recipients.map((r) => (
                  <span key={r} className="inline-flex items-center gap-1 rounded-full border border-[#2ECC9A]/30 bg-[#2ECC9A]/10 px-2 py-0.5 text-xs text-[#1A3A5C]" data-testid={`monthly-pack-chip-${r}`}>
                    {r}
                    <button type="button" onClick={() => setRecipients((s) => s.filter((x) => x !== r))} className="text-slate-400 hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={includeEsign} onChange={(e) => setIncludeEsign(e.target.checked)} data-testid="monthly-pack-esign-toggle" />
              Include eSign performance PDF
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="monthly-pack-cancel">Cancel</Button>
              <Button size="sm" onClick={send} disabled={sending || recipients.length === 0} data-testid="monthly-pack-send" className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                {sending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                Send to {recipients.length || 0}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatementsButton() {
  const [open, setOpen] = React.useState(false);
  const [candidates, setCandidates] = React.useState([]);
  const [selected, setSelected] = React.useState(new Set());
  const [loading, setLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [tone, setTone] = React.useState('friendly');
  const [paymentLink, setPaymentLink] = React.useState('');

  const load = async () => {
    setLoading(true);
    try { const r = await statements.candidates(); setCandidates(r.items || []); setSelected(new Set((r.items || []).map((c) => c.id))); }
    catch { toast.error('Could not load contact list'); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { if (open) load(); }, [open]);

  const toggle = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const send = async () => {
    if (selected.size === 0) { toast.error('Select at least one client'); return; }
    setSending(true);
    try {
      const r = await statements.send({
        contact_ids: Array.from(selected),
        tone,
        payment_link: paymentLink.trim() || null,
      });
      toast.success(`Sent ${r.sent_count}/${r.attempted} statement(s) for ${r.label}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => setOpen(true)} data-testid="analytics-statements-btn">
        <FileText className="mr-1.5 h-3.5 w-3.5" /> Send statements
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="statements-dialog">
          <DialogHeader><DialogTitle className="text-[#1A3A5C]">Send monthly client statements</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Emails last month&apos;s statement PDF to each selected client — invoices, payments and trust movements.
            Auto-sends on the 1st of every month; use this dialog to trigger it manually or resend.
          </p>
          <div className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 bg-slate-50/40 p-3">
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="statements-tone"
                      className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs">
                <option value="friendly">Friendly (default)</option>
                <option value="firm">Firm (7-day notice)</option>
                <option value="formal">Formal (14-day notice)</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Payment link (optional)</label>
              <input value={paymentLink} onChange={(e) => setPaymentLink(e.target.value)}
                     placeholder="https://buy.stripe.com/…"
                     data-testid="statements-payment-link"
                     className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs" />
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" /></div>
          ) : candidates.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500" data-testid="statements-empty">
              No clients with linked matters yet. Add a contact and attach a matter first.
            </div>
          ) : (
            <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="statements-candidates">
              {candidates.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-slate-50" data-testid={`statement-row-${c.id}`}>
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`statement-check-${c.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#1A3A5C]">{c.name || c.email}</div>
                    <div className="text-[11px] text-slate-500">{c.email} · {c.matter_count} matter(s)</div>
                  </div>
                  <a
                    href={statements.previewPdfUrl(c.id)}
                    target="_blank" rel="noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      const t = localStorage.getItem('immipro_token');
                      fetch(statements.previewPdfUrl(c.id), { headers: t ? { Authorization: `Bearer ${t}` } : {} })
                        .then((r) => r.blob()).then((blob) => {
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        })
                        .catch(() => toast.error('Preview failed'));
                    }}
                    className="text-[10px] font-semibold uppercase tracking-wider text-[#2ECC9A] hover:underline"
                    data-testid={`statement-preview-${c.id}`}
                  >Preview</a>
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="statements-cancel">Cancel</Button>
            <Button size="sm" onClick={send} disabled={sending || selected.size === 0} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90" data-testid="statements-send">
              {sending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              Send to {selected.size || 0}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
